import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import { useEnvStore } from '@gik/core/store/EnvStore';
import { useUserStore } from '@gik/core/store/UserStore';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import NetworkStore from '@gik/core/stores/NetworkStore.ts';
import { ResourceStatus } from '@gik/core/stores/NetworkStore.ts';
import type { UIComponent } from '@gik/core/types/UI';
import { cn } from '@gik/core/utils/classnames';
import { Button } from '@gik/ui-legacy/Button';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import { LineClamp } from '@gik/ui-legacy/LineClamp';
import { ApiErrorDisplay } from '@gik/ui-legacy/gik/ApiErrorDisplay';
import ChevronDownIcon from '@heroicons/react/outline/ChevronDownIcon';
import he from 'he';
import React from 'react';
import { useUserWallets } from '../../hooks/useUserWallets';

export type UserWalletsProps = UIComponent & {};

export const UserWallets = ({ className, ...otherProps }: UserWalletsProps) => {
  const userId = useUserStore(state => state.id);
  const isWalletRequestEnabled = useEnvStore.getState().WALLET_ENABLED && !!userId;
  const requestKey = isWalletRequestEnabled ? `user-wallets-${userId}` : '';
  const { data: wallets, error, mutate } = useUserWallets(
    isWalletRequestEnabled ? userId : undefined,
    isWalletRequestEnabled ? { requestKey } : undefined,
  );

  // Subscribe only when this request is active to avoid stale error state when wallets are disabled.
  const requestStatus = React.useSyncExternalStore(
    callback => (isWalletRequestEnabled ? NetworkStore.subscribe(callback) : () => {}),
    () => (isWalletRequestEnabled ? NetworkStoreApi.getEntry(requestKey).status : ResourceStatus.Idle),
  );

  const isRequestError = requestStatus === ResourceStatus.Failed;
  const isRequestRetrying =
    requestStatus === ResourceStatus.Retry || requestStatus === ResourceStatus.Loading;
  const shouldShowImmediateError = isWalletRequestEnabled && !!error && !wallets && !isRequestRetrying;

  const tileClasses =
    'tw-cursor-pointer tw-px-3 tw-py-6 tw-shadow-lg tw-text-lg tw-text-white tw-font-semibold tw-flex tw-rounded';

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const trackEvent = React.useCallback((customerId: string) => {
    Analytics.fireEvent(AnalyticsEvents.WalletClickWalletButton, {
      customerId,
    });
  }, []);

  // Only surface the error UI after all SWR retries are exhausted.
  // During retries the NetworkStore status is 'retrying', not 'failed', so we keep
  // rendering the last-known wallet data (SWR preserves stale data across failures).
  if (isRequestError || shouldShowImmediateError) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
        <ApiErrorDisplay errors={error ? [error] : []} />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            NetworkStoreApi.reload(requestKey);
            mutate();
          }}
        >
          Reload
        </Button>
      </div>
    );
  }

  return (
    <div {...otherProps} className={cn(className, 'tw-flex tw-flex-col tw-gap-4')}>
      {wallets?.map((wallet, index) => {
        if (!expanded && index >= 2) return null;
        if (!wallet) return null;

        return (
          <HoverAnimation key={wallet.id}>
            <div onClick={() => trackEvent(wallet.customerId)}>
              <a href={`/wallets/${wallet.customerId}`}>
                <div className={cn(`gik-gradient-aqua tw-gap-2`, tileClasses)}>
                  <div className="tw-font-heading">
                    <LineClamp lines={3} showButton={false} fadeOut={false}>
                      {he.decode(wallet.title)}
                    </LineClamp>
                  </div>
                  <div className="tw-text-2xl tw-text-right tw-flex-1 ">
                    <div className="">{wallet.unredeemedGiftsCount}</div>
                    <div className="tw-uppercase tw-text-xs tw-font-semibold tw-whitespace-nowrap">
                      <span>{wallet.unredeemedGiftsCount === 1 ? 'Gift' : 'Gifts'}</span> to redeem
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </HoverAnimation>
        );
      })}

      {!expanded && wallets?.length > 2 && (
        <div className="tw-text-right">
          <Button variant="secondary" size="xs" pill append={<ChevronDownIcon />} onClick={() => setExpanded(true)}>
            See All
          </Button>
        </div>
      )}
      {/* {expanded && (
        <div className="tw-text-right">
          <Button variant="secondary" size="xs" pill append={<ChevronUp />} onClick={() => setExpanded(false)}>
            See Less
          </Button>
        </div>
      )} */}
    </div>
  );
};
